import * as React from 'react';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { Bars, EmptyState, currency, number } from './ui';
import { TRUE_COLORS } from '../theme';

/** Cash that came in or went out of a register outside a sale, as api/movements/ sends it. */
export type CashMovement = {
  id: number; amount: number; date: string;
  /** Why (empty on movements from before it, and from registers that don't ask yet). */
  reason?: string | null;
  user_name?: string | null; checkout_no?: number | null; location_name?: string | null;
};

const NO_REASON = 'Sin motivo';

/** Each reason with what moved for it, the most first (the same words in any case count as one). */
const byReason = (movements: CashMovement[]) => {
  const groups = new Map<string, { label: string; value: number; count: number }>();
  for (const movement of movements) {
    const reason = (movement.reason ?? '').trim() || NO_REASON;
    const label = reason[0].toUpperCase() + reason.slice(1);
    const group = groups.get(label.toLowerCase()) ?? { label, value: 0, count: 0 };
    group.value += Math.abs(movement.amount);
    group.count += 1;
    groups.set(label.toLowerCase(), group);
  }
  return [...groups.values()].sort((a, b) => b.value - a.value)
    .map(group => ({ label: group.label, value: group.value, detail: group.count === 1 ? '1 vez' : `${number(group.count)} veces` }));
};

/** Cash in, cash out and the net of a period, and what each went on. */
export const CashSummary = ({ movements }: { movements: CashMovement[] }) => {
  const inflows = movements.filter(m => m.amount > 0);
  const outflows = movements.filter(m => m.amount < 0);
  const cashIn = inflows.reduce((sum, m) => sum + m.amount, 0);
  const cashOut = outflows.reduce((sum, m) => sum - m.amount, 0);
  const net = cashIn - cashOut;
  return (
    <Stack spacing={1.25} data-testid="cash-summary">
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Entradas</Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{currency(cashIn)}</Typography>
      </Stack>
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Salidas</Typography>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: cashOut > 0 ? TRUE_COLORS.danger : 'text.primary' }}>{cashOut > 0 ? '−' : ''}{currency(cashOut)}</Typography>
      </Stack>
      <Box sx={{ borderTop: `1px solid ${TRUE_COLORS.borderSolid}`, pt: 1.5, mt: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography sx={{ fontWeight: 700 }}>Neto</Typography>
        <Typography data-testid="cash-net" sx={{ fontWeight: 700, fontSize: '1.125rem', color: net < 0 ? TRUE_COLORS.danger : 'text.primary' }}>{currency(net)}</Typography>
      </Box>
      {outflows.length > 0 && (
        <Box sx={{ pt: 1.5 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>Salidas por motivo</Typography>
          <Bars rows={byReason(outflows)} format={currency} />
        </Box>
      )}
      {inflows.length > 0 && (
        <Box sx={{ pt: 1.5 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>Entradas por motivo</Typography>
          <Bars rows={byReason(inflows)} format={currency} />
        </Box>
      )}
    </Stack>
  );
};

/** Each movement: when, why, who and at which register, the latest first. */
export const CashList = ({ movements, limit = 10 }: { movements: CashMovement[]; limit?: number }) => {
  const [all, setAll] = React.useState(false);
  if (movements.length === 0) return <EmptyState compact title="Sin movimientos de efectivo en este periodo" />;
  const shown = all ? movements : movements.slice(0, limit);
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" data-testid="cash-movements">
        <TableHead>
          <TableRow>
            <TableCell>Fecha</TableCell>
            <TableCell>Motivo</TableCell>
            <TableCell>Quién</TableCell>
            <TableCell>Caja</TableCell>
            <TableCell align="right">Monto</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map(movement => (
            <TableRow key={movement.id} hover>
              <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{dayjs(movement.date).format('D MMM YYYY, HH:mm')}</TableCell>
              <TableCell sx={{ fontWeight: movement.reason ? 500 : 400, color: movement.reason ? 'text.primary' : 'text.secondary', minWidth: 160 }}>{movement.reason || NO_REASON}</TableCell>
              <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{movement.user_name || '—'}</TableCell>
              <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                {[movement.location_name, movement.checkout_no != null ? `Caja ${movement.checkout_no}` : null].filter(Boolean).join(' · ') || '—'}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: movement.amount < 0 ? TRUE_COLORS.danger : TRUE_COLORS.success }}>
                {movement.amount < 0 ? '−' : '+'}{currency(Math.abs(movement.amount))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {movements.length > limit && (
        <Box sx={{ px: 2, py: 1 }}>
          <Link component="button" variant="body2" onClick={() => setAll(v => !v)}>
            {all ? 'Ver menos' : `Ver los ${number(movements.length)} movimientos`}
          </Link>
        </Box>
      )}
    </Box>
  );
};
